/**
 * Client List Component
 *
 * Displays a list of clients fetched from the authenticated API.
 * Shows loading state, errors, and client data.
 */

import React from 'react';
import {
  Card,
  Flex,
  useTheme,
  spacing,
  Button,
} from '@fti/design-system';
import { useClients } from '../api/clients';
import type { ApiError } from '../api/types';

/**
 * Error display component for API errors.
 */
function ErrorDisplay({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  const { theme } = useTheme();
  const errorColor = theme.colors.semantic.error.default;
  const textColor = theme.colors.text.primary;

  return (
    <Card variant="outlined" padding="md">
      <Flex direction="column" align="center" gap="md">
        <div style={{
          color: errorColor,
          fontSize: '1.5rem',
          fontWeight: 600
        }}>
          {error.isAuthError ? '🔒' : '⚠️'}
        </div>
        <div style={{
          color: textColor,
          textAlign: 'center',
          fontSize: '0.95rem'
        }}>
          {error.message}
        </div>
        {error.canRetry && (
          <Button variant="outline" onClick={onRetry}>
            Try Again
          </Button>
        )}
        {error.isAuthError && (
          <div style={{
            color: theme.colors.text.secondary,
            fontSize: '0.85rem',
            textAlign: 'center'
          }}>
            You may need to sign in again.
          </div>
        )}
      </Flex>
    </Card>
  );
}

/**
 * Loading indicator component.
 */
function LoadingIndicator() {
  const { theme } = useTheme();

  return (
    <Card variant="outlined" padding="md">
      <Flex direction="column" align="center" gap="sm">
        <div style={{
          color: theme.colors.text.secondary,
          fontSize: '1.2rem'
        }}>
          Loading clients...
        </div>
        <div style={{
          width: '24px',
          height: '24px',
          border: `3px solid ${theme.colors.border.default}`,
          borderTopColor: theme.colors.primary[500],
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </Flex>
    </Card>
  );
}

/**
 * Empty state when no clients exist.
 */
function EmptyState() {
  const { theme } = useTheme();

  return (
    <Card variant="outlined" padding="md">
      <Flex direction="column" align="center" gap="sm">
        <div style={{
          color: theme.colors.text.secondary,
          fontSize: '1.2rem'
        }}>
          No clients found
        </div>
        <div style={{
          color: theme.colors.text.secondary,
          fontSize: '0.85rem'
        }}>
          There are no clients in the system yet.
        </div>
      </Flex>
    </Card>
  );
}

/**
 * Client list component.
 *
 * Fetches and displays clients from the authenticated API.
 */
export function ClientList() {
  const { data: clients, loading, error, refetch } = useClients({ limit: 10 });
  const { theme } = useTheme();

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={refetch} />;
  }

  if (!clients || clients.length === 0) {
    return <EmptyState />;
  }

  return (
    <Card variant="outlined" padding="md">
      <Flex justify="between" align="center" style={{ marginBottom: spacing.md }}>
        <h3 style={{
          margin: 0,
          color: textColor,
          fontSize: '1.1rem',
          fontWeight: 600
        }}>
          Clients ({clients.length})
        </h3>
        <Button variant="ghost" size="sm" onClick={refetch}>
          ↻ Refresh
        </Button>
      </Flex>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.sm
      }}>
        {clients.map((client) => (
          <div
            key={client.id}
            style={{
              padding: spacing.sm,
              borderRadius: '4px',
              border: `1px solid ${borderColor}`,
              backgroundColor: theme.colors.background.paper
            }}
          >
            <Flex justify="between" align="start">
              <div>
                <div style={{
                  fontWeight: 600,
                  color: textColor,
                  fontSize: '0.95rem'
                }}>
                  {client.name}
                </div>
                <div style={{
                  color: mutedColor,
                  fontSize: '0.8rem',
                  fontFamily: 'monospace'
                }}>
                  {client.clientcode}
                </div>
              </div>
              <div style={{
                color: mutedColor,
                fontSize: '0.75rem',
                textAlign: 'right'
              }}>
                ID: {client.id}
                {client.creator && (
                  <div>by {client.creator}</div>
                )}
              </div>
            </Flex>
          </div>
        ))}
      </div>
    </Card>
  );
}
